import { NetworkStatusOptions, USStateOptions } from '@strata/constants';
import { FormContainer } from '@strata/react-form-container';
import { PhoneInputFormik, TextInputFormik } from '@strata/react-inputs';
import { SuggestMultiSelectFormik, SuggestSingleSelectFormik } from '@strata/react-selects';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';

import { PayToInformationFields } from './PayToInformationFields';

import { CREATE_ROUTE } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { post, put, useGet } from 'services/Http';
import { loadSupplierValues, packageSupplier, supplierChildSchema } from 'services/NPISupport';

export const EditChildSupplier = () => {
    const { childNpi, npi } = useParams();
    const navigate = useNavigate();

    const goBack = () =>
        navigate({
            pathname: `/edit-supplier/${npi}`,
            hash: 'supplier-locations',
        });

    const isCreate = childNpi === CREATE_ROUTE;

    const supplier = useGet(!isCreate ? `/reference/suppliers/${childNpi}` : null);
    const title =
        !isCreate && supplier?.organization_name
            ? `Edit Child Supplier: ${supplier.organization_name} (${childNpi})`
            : `${isCreate ? 'Add' : 'Edit'} Child Supplier`;

    const taxonomyCodes = useGet('/reference/taxonomies/');
    const taxonomyOptions = useMemo(
        () =>
            (taxonomyCodes ?? []).map((taxonomy) => ({
                ...taxonomy,
                name: taxonomy.taxonomy_description,
                secondaryData: taxonomy.taxonomy_code,
            })),
        [taxonomyCodes],
    );

    return (
        <Formik
            initialValues={loadSupplierValues(supplier, taxonomyOptions)}
            validationSchema={supplierChildSchema}
            enableReinitialize
            onSubmit={async (values) => {
                try {
                    const body = packageSupplier(values);
                    body.parent_supplier_npi = npi;
                    if (isCreate) {
                        await post('/reference/suppliers/', body);
                    } else {
                        await put(`/reference/suppliers/${values.npi}`, body);
                    }
                    goBack();
                } catch (err) {
                    handleError('Error saving supplier.', err);
                }
            }}
        >
            {(formik) => (
                <FormContainer
                    formTitle={title}
                    isLoading={!isCreate && !(supplier && taxonomyCodes)}
                    onFormSubmit={formik.handleSubmit}
                    isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                    onSecondaryButtonClick={goBack}
                    formLeftContent={
                        <>
                            <div className="margin__bottom--medium font-bold--uppercase">
                                Organization Information
                            </div>
                            <TextInputFormik
                                label="Supplier NPI"
                                id="npi"
                                required
                                disabled={!isCreate}
                            />
                            <SuggestSingleSelectFormik
                                id="network_status"
                                label="Network Status"
                                required
                                values={NetworkStatusOptions}
                                onChange={(newValue) =>
                                    formik.setFieldValue('network_status', newValue.value)
                                }
                                selectedItem={NetworkStatusOptions.find(
                                    ({ value }) => value === formik.values.network_status,
                                )}
                            />
                            <TextInputFormik label="NCPDP ID" id="ncpdp_id" required />
                            <TextInputFormik
                                label="Organization Name"
                                id="organization_name"
                                required
                            />
                            <PhoneInputFormik label="Phone Number" id="telephone_number" required />
                            <PhoneInputFormik label="Fax Number" id="fax_number" />
                            <TextInputFormik label="Address Line 1" id="address_1" required />
                            <TextInputFormik label="Address Line 2" id="address_2" />
                            <TextInputFormik label="City" id="city" required />
                            <SuggestSingleSelectFormik
                                label="State"
                                id="state"
                                required
                                values={USStateOptions}
                                selectedItem={USStateOptions.find(
                                    ({ secondaryData }) => secondaryData === formik.values.state,
                                )}
                                onChange={(newVal) =>
                                    formik.setFieldValue('state', newVal?.secondaryData)
                                }
                            />
                            <TextInputFormik label="Postal Code" id="postal_code" required />
                            <TextInputFormik label="Tax ID" id="tax_id" required />

                            <div className="margin__bottom--medium font-bold--uppercase">
                                Operations Contact Information
                            </div>
                            <span className="font-regular--uppercase margin__bottom--medium">
                                Primary Contact
                            </span>
                            <TextInputFormik
                                label="Primary Contact First Name"
                                id="first_name"
                                required
                            />
                            <TextInputFormik
                                label="Primary Contact Last Name"
                                id="last_name"
                                required
                            />

                            <PayToInformationFields />

                            <div className="margin__bottom--medium font-bold--uppercase">
                                Additional Information
                            </div>
                            <TextInputFormik label="Taxonomy Code" id="taxonomy_code" disabled />
                            <TextInputFormik label="Classification" id="classification" disabled />
                            <TextInputFormik label="Specialization" id="specialization" disabled />
                            <SuggestMultiSelectFormik
                                label="Taxonomy Codes"
                                id="taxonomies"
                                isLoading={!taxonomyCodes}
                                values={taxonomyOptions}
                            />
                        </>
                    }
                />
            )}
        </Formik>
    );
};
